import React from "react";
import { C1SignupData } from "../C1SignupFlow";
import { useGoLive, mapPlanToTier } from "../../../hooks/useGoLive";
import { BILLING_PLANS } from "../../../config/billingConfig";
import { resolveEntityType } from "../../../hooks/useAbnLookup";
import { deleteRegistrationSession } from "../../../services/registrationSessionService";
import {
  LoadingSpinner,
  AlertBox,
  SummaryBox,
  CredentialDisplay,
} from "../../shared";
import { InfoGrid } from "../components";

// Local storage keys the signup wizard persists — mirrors C1SignupFlow.tsx / useStripePayment /
// registrationSessionService. Kept in one place so "New Signup" can clear them all at once.
const SIGNUP_STORAGE_KEY = 'c1_signup_form_data';
const REG_SESSION_TOKEN_KEY = 'reg_session_token';
const PAYMENT_STATE_KEY = 'c1_payment_state';

interface StepProps {
  data: C1SignupData;
  onNext: (stepData: Partial<C1SignupData>) => void;
  onCancel: () => void;
}

const GoLiveC1: React.FC<StepProps> = ({ data, onNext, onCancel }) => {
  const {
    onboardingState,
    onboardingResult,
    processingStep,
    handleGoLive,
    handleRetry,
    getCredits,
  } = useGoLive(data);

  const handleComplete = () => {
    onNext({ goLiveStatus: "approved" });
  };

  // Render processing state
  if (onboardingState === "processing") {
    return (
      <div className="step-form">
        <div
          className="processing-container"
          style={{ textAlign: "center", padding: "60px 20px" }}
        >
          <LoadingSpinner
            size={60}
            color="#0066cc"
            trackColor="#e0e0e0"
            label={processingStep}
          />
          <h2 style={{ margin: "24px 0 0", color: "#333" }}>
            Setting Up Your Account
          </h2>
        </div>
      </div>
    );
  }

  // Render success state
  if (onboardingState === "success" && onboardingResult) {
    return (
      <div className="step-form">
        <div className="success-container">
          {/* Hero header */}
          <div className="golive-hero">
            <div className="golive-hero__icon">🎉</div>
            <h2 className="golive-hero__title">Account Created Successfully!</h2>
            <p className="golive-hero__subtitle">
              Your ARCaml account is now active and ready to use.
            </p>
          </div>

          {/* Credentials Box */}
          <CredentialDisplay
            fields={[
              { label: "USERNAME", value: onboardingResult.loginId || "", copyable: true },
              { label: "EMAIL", value: onboardingResult.email || "" },
              {
                label: "Temporary Password",
                value: onboardingResult.temporaryPassword || "",
                highlight: true,
                copyable: true,
              },
            ]}
            warning="Please save these credentials. You will be required to change your password on first login."
            style={{ marginBottom: 16 }}
          />

          {/* Email Confirmation */}
          <AlertBox
            variant="success"
            icon="📧"
            title="Welcome email sent!"
            style={{ marginBottom: 16 }}
          >
            We've sent your login credentials to {onboardingResult.email}
          </AlertBox>

          {/* Account Summary */}
          <SummaryBox
            title="Account Summary"
            items={[
              { label: "Organization", value: data.partyName },
              { label: "ABN", value: data.partyAbn || data.partyAcn || "-" },
              {
                label: "Package Purchased",
                value:
                  mapPlanToTier(data.stripePlanType).charAt(0).toUpperCase() +
                  mapPlanToTier(data.stripePlanType).slice(1),
                valueColor: "#16A34A",
              },
              {
                label: "Credits",
                value: `${getCredits()} credits`,
              },
            ]}
            style={{ marginBottom: 28 }}
          />

          {/* Action Buttons */}
          <div className="golive-actions">
            <a
              href={onboardingResult.loginUrl || "/login"}
              className="golive-actions__login"
            >
              🚀 Go to Login
            </a>
            <button
              data-testid="golive-new-signup-btn"
              onClick={async () => {
                // Try the server-side delete first — best effort. If it fails (network hiccup,
                // stale token, backend error) we still fully clear the client below, so the next
                // page load cannot silently resume the completed session.
                try { await deleteRegistrationSession(); } catch { /* proceed anyway */ }

                // Hard-clear every localStorage key the wizard writes. Previously only the form
                // data key was removed, which left the registration session token in place — the
                // next boot still authenticated the server-side session and hydrated the user
                // straight back into this Go Live page.
                try {
                  localStorage.removeItem(SIGNUP_STORAGE_KEY);
                  localStorage.removeItem(REG_SESSION_TOKEN_KEY);
                  localStorage.removeItem(PAYMENT_STATE_KEY);
                } catch { /* ignore */ }

                // Reload with a fresh=1 flag so C1SignupFlow skips its resume probe for this one
                // boot. Belt-and-braces: even if the HttpOnly session cookie is still present,
                // the flow will not consult it. The flag is stripped from the URL on arrival.
                window.location.href = window.location.pathname + '?fresh=1';
              }}
              className="golive-actions__new-signup"
            >
              ➕ New Signup
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (onboardingState === "error") {
    return (
      <div className="step-form">
        <div
          className="error-container"
          style={{ textAlign: "center", padding: "40px 20px" }}
        >
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>❌</div>
          <h2 style={{ color: "#dc3545", margin: "0 0 8px 0" }}>
            Account Setup Failed
          </h2>
          <p style={{ color: "#666", marginBottom: "24px" }}>
            {onboardingResult?.error ||
              "An error occurred while setting up your account."}
          </p>
          <AlertBox
            variant="danger"
            title="What to do:"
            style={{ marginBottom: 24, textAlign: "left" }}
          >
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Check your internet connection and try again</li>
              <li>Ensure all information is correct</li>
              <li>Contact support if the problem persists</li>
            </ul>
          </AlertBox>
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "center" }}
          >
            <button
              className="btn-primary"
              onClick={handleRetry}
              style={{
                background: "linear-gradient(135deg, #0066cc 0%, #0052a3 100%)",
              }}
            >
              🔄 Try Again
            </button>
            <button
              className="btn-secondary"
              onClick={() =>
                (window.location.href = "mailto:support@arcaml.com.au")
              }
            >
              📧 Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render ready state
  return (
    <div className="step-form">
      {/* Engagement Setup Summary */}
      <InfoGrid
        title="Engagement Setup Summary"
        items={[
          { label: "Party Name", value: (
            <>
              {data.partyName || "—"}
              {data.partyEntityType && (
                <span style={{ fontSize: 12, fontWeight: 500, color: "#64748B", marginLeft: 6 }}>({resolveEntityType(data.partyEntityType) || data.partyEntityType})</span>
              )}
            </>
          ), icon: "🏢" },
          { label: "Party ABN", value: data.partyAbn || data.partyAcn || "—", icon: "🔢" },
          { label: "Contact", value: data.partyEmail || "—", icon: "✉️" },
        ]}
        columns={3}
        style={{ fontSize: 13 }}
      />

      {/* Subscription Details */}
      <div style={{
        background: "#fff",
        border: "1.5px solid #E2E8F0",
        borderRadius: 16,
        padding: "20px 24px",
        marginBottom: 16,
        boxShadow: "0 2px 12px rgba(27,58,107,0.06)",
      }}>
        <h3 style={{
          margin: "0 0 16px",
          fontSize: 13, fontWeight: 700,
          color: "#1B3A6B",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}>
          Subscription Details
        </h3>
        <div className="info-grid__cards" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{
            background: "#F0FDF4", border: "1.5px solid #86EFAC",
            borderTop: "3px solid #16A34A", borderRadius: 12, padding: "14px 16px",
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 6 }}>Package Purchased</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 17 }}>💳</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#16A34A" }}>
                {mapPlanToTier(data.stripePlanType).charAt(0).toUpperCase() + mapPlanToTier(data.stripePlanType).slice(1)}
              </span>
              <span style={{
                fontSize: 11, fontWeight: 700, background: "#16A34A", color: "#fff",
                padding: "2px 8px", borderRadius: 20,
              }}>Active</span>
            </div>
          </div>
          <div style={{
            background: "#F8FAFC", border: "1.5px solid #E2E8F0",
            borderTop: "3px solid #1B3A6B", borderRadius: 12, padding: "14px 16px",
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 6 }}>Credits Included</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 17 }}>🪙</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1B3A6B" }}>
                {getCredits()} credits
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div style={{
        background: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
        border: "1.5px solid #86EFAC",
        borderRadius: 12,
        padding: "16px 22px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "#16A34A",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
          boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
        }}>✓</div>
        <div>
          <strong style={{ color: "#15803D", fontSize: 15, fontWeight: 700, display: "block" }}>
            Account Ready for CDD Coverage
          </strong>
          <span style={{ color: "#166534", fontSize: 13, lineHeight: 1.5 }}>
            All required information has been collected and validated. Click below to activate your account.
          </span>
        </div>
      </div>

      {/* What Happens Next */}
      <div style={{
        background: "#fff",
        border: "1.5px solid #E2E8F0",
        borderRadius: 12,
        padding: "18px 22px",
        marginBottom: 28,
        boxShadow: "0 2px 12px rgba(27,58,107,0.04)",
      }}>
        <strong style={{
          display: "block", marginBottom: 14,
          fontSize: 13, fontWeight: 700,
          color: "#1B3A6B", textTransform: "uppercase", letterSpacing: "0.07em",
        }}>
          What happens when you Begin CDD Coverage
        </strong>
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {[
            { icon: "🏢", text: "Your organization account will be created" },
            { icon: "👤", text: "Your admin user account will be provisioned" },
            { icon: "💳", text: "Your subscription will be activated" },
            { icon: "📧", text: <span>A welcome email with login credentials will be sent to <strong style={{ color: "#1B3A6B" }}>{data.partyEmail}</strong></span> },
            { icon: "🚀", text: "You can start onboarding customers immediately" },
          ].map((item, i, arr) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "11px 0",
              borderBottom: i < arr.length - 1 ? "1px solid #F1F5F9" : "none",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: "#F0FDF4", border: "1px solid #BBF7D0",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, flexShrink: 0,
              }}>{item.icon}</div>
              <span style={{ fontSize: 14, color: "#334155", lineHeight: 1.5 }}>{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          data-testid="golive-activate-btn"
          onClick={handleGoLive}
          style={{
            background: "linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)",
            color: "#fff",
            border: "none",
            padding: "15px 48px",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase" as const,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(27,58,107,0.4)",
            transition: "all 0.2s ease",
          }}
        >
          🚀 Begin Activation
        </button>
      </div>
    </div>
  );
};

export default GoLiveC1;
